import React from 'react'
import { useLazyQuery } from '@apollo/react-hooks'

import { useEnterpriseContext, useI18n } from '@thg-commerce/enterprise-core'
import { BundleSku } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/ResponsiveBuildYourOwnBundleResolver'
import { productDescription } from '@thg-commerce/enterprise-network/src/transformers/product/description'
import { Modal } from '@thg-commerce/gravity-elements'
import { ProductDescriptionAccordion } from '@thg-commerce/gravity-patterns'
import { MAX_QUANTITY_SELECTION } from '@thg-commerce/widget-build-your-own-bundle'

import { Product as PRODUCT_DESCRIPTION_QUERY } from '../../widgets/graphql/Query/Product.graphql'

import {
  computeMaxQuantity,
  handleProductSelection,
  handleUpdateQuantity,
  INITIAL_QUANTITY,
  removeProduct,
} from './helpers'
import { BundleProduct } from './ResponsiveBuildYourOwnBundleProducts'
import {
  CloseIcon,
  IconContainer,
  InfoIconButton,
  OutOfStock,
  OutOfStockSubtitle,
  OutOfStockTitle,
  QuantityWrapper,
  StyledIconInfo,
  StyledProductBlock,
  StyledQuantitySelector,
  SubTitle,
  Title,
} from './styles'

export interface BundleProductBlockProps {
  product: BundleSku
  enableQuantitySelector?: boolean
  maxProductsPerCategory: number
  numberOfProductsInSelectedCategory: number
  selectedBundleProducts: BundleProduct[]
  categoryId: string
  setSelectedBundleProducts: (
    callback: (currentProducts: BundleProduct[]) => BundleProduct[],
  ) => void
  availableBundleQuantitySelection: number
}

interface DescriptionQueryResponse {
  product: {
    sku: number
    title: string
    content: {
      key: string
      value:
        | {
            stringListValue: string[]
          }
        | { richContentValue: { content: { content: string }[] } }
    }[]
  }
}

interface DescriptionQueryVariables {
  sku: number
  keys: {
    items: string[]
    details: string[]
    download: string[]
    video: string[]
  }
}

export const BundleProductCard = (props: BundleProductBlockProps) => {
  const i18n = useI18n()
  const {
    appConfig: {
      contentKeys: {
        productDescriptionKeys = {
          items: [],
          details: [],
          video: [],
          download: [],
        },
      } = {},
    },
  } = useEnterpriseContext()

  const [openInfoModal, setOpenInfoModal] = React.useState(false)

  const [selectedQuantity, setSelectedQuantity] = React.useState<number>(
    INITIAL_QUANTITY,
  )

  const [loadProductDescription, { data }] = useLazyQuery<
    DescriptionQueryResponse,
    DescriptionQueryVariables
  >(PRODUCT_DESCRIPTION_QUERY)

  const i18nText = {
    reviewsStarsLabel: i18n('reviews.stars.label'),
    reviewsLabel: i18n('reviews.reviews.label'),
    productInfoTitle: i18n('product.description.accordion.title'),
    productInfoUseAccordion: i18n('product.description.accordion.useaccordion'),
    soldOutText: i18n('general.productsoldout.text'),
    backInStockSoon: i18n('widget.buildyourbundle.backinstocksoon.text'),
    productCategoryTitle: i18n('product.content.productdetails.title.text'),
    closeAriaLabel: i18n('general.modal.close.button.arialabel'),
    closeLabel: i18n('general.modal.close.button.label'),
    productInfoAriaLabel: i18n('product.description.accordion.title'),
    rrpText: i18n('widget.buildyourbundle.rrp.text'),
    quantitySelectorTexts: {
      decreaseButtonLabel: i18n('general.quantity.decrease'),
      increaseButtonLabel: i18n('general.quantity.increase'),
      quantityLabel: i18n('general.quantity.text'),
    },
    alternateImageText: i18n('widget.buildyourownbundle.alternateImage.text'),
  }

  const productsInCategory = props.selectedBundleProducts.filter(
    (selectedProduct) => {
      return selectedProduct.categoryId === props.categoryId
    },
  )

  const isProductSelected = props.selectedBundleProducts.some(
    (selectedProduct) => selectedProduct.sku === props.product.sku,
  )

  const maxBundleQuantityReached = props.availableBundleQuantitySelection === 0

  const maxPerOrder = props.product.maxPerOrder || MAX_QUANTITY_SELECTION
  const productAvailableQuantity = maxPerOrder - selectedQuantity

  const computedMaxQuantity = computeMaxQuantity(
    props.maxProductsPerCategory,
    props.numberOfProductsInSelectedCategory,
    selectedQuantity,
    props.availableBundleQuantitySelection,
  )

  const maxQuantitySelectorValue = Math.min(computedMaxQuantity, maxPerOrder)

  const quantitySelector = {
    i18nText: i18nText.quantitySelectorTexts,
    maxValue: maxQuantitySelectorValue,
    minValue: 1,
    editable: true,
    quantity: selectedQuantity,
  }

  const isQuantitySelectorEnabled =
    props.enableQuantitySelector &&
    isProductSelected &&
    props.maxProductsPerCategory > 1 &&
    productAvailableQuantity >= 0

  const productDescriptionInfo = data
    ? productDescription(data.product, productDescriptionKeys)
    : null

  const itemsWithTitle =
    productDescriptionInfo?.items.map((item: { titleKey: any }) => ({
      ...item,
      title: i18n(`product.content.${item.titleKey}.title.text`),
    })) || []

  const detailsWithTitle =
    productDescriptionInfo?.details.map((item) => ({
      ...item,
      title: i18n(`product.content.${item.titleKey}.title.text`),
    })) || []

  const screenReaderReviewText = ({ score, total }) =>
    `${score} ${i18nText.reviewsStarsLabel} ${total} ${i18nText.reviewsLabel}`

  return (
    <div style={{ position: 'relative', height: '100%' }}>
      <StyledProductBlock
        {...props.product}
        removeATags={true}
        marketedSpecialOffer={undefined}
        content={undefined}
        icons={undefined}
        url={''}
        button={{
          title: '',
          disabled: true,
          disableHref: true,
        }}
        review={
          props.product?.reviews
            ? {
                starRating: props.product.reviews.averageScore,
                numberOfReviews: props.product.reviews.total,
                screenReaderOnlyText: screenReaderReviewText({
                  score: props.product.reviews.averageScore,
                  total: props.product.reviews.total,
                }),
              }
            : null
        }
        disableUrl={true}
        attributes={{ justifyContent: 'start', alignItems: 'start' }}
        selectedProduct={isProductSelected}
        onClickEventEmitter={() => {
          props.product.inStock &&
            handleProductSelection(
              props.product.sku,
              props.categoryId,
              props.setSelectedBundleProducts,
              props.maxProductsPerCategory,
              Number(props.product?.price?.price.amount),
              maxBundleQuantityReached,
              productsInCategory,
              isProductSelected,
              props.numberOfProductsInSelectedCategory,
              setSelectedQuantity,
            )
        }}
        notSelectable={!props.product.inStock}
        maxProductsSelected={
          maxBundleQuantityReached ||
          props.maxProductsPerCategory ===
            props.numberOfProductsInSelectedCategory
        }
        title={{ value: props.product.title || '', useAlternateStyle: false }}
        isProductSelected={isQuantitySelectorEnabled}
      />
      {!props.product.inStock && (
        <OutOfStock>
          <OutOfStockTitle>{i18nText.soldOutText}</OutOfStockTitle>
          <OutOfStockSubtitle>{i18nText.backInStockSoon}</OutOfStockSubtitle>
        </OutOfStock>
      )}
      {isProductSelected && (
        <IconContainer>
          <CloseIcon
            onClick={() =>
              removeProduct(props.product.sku!, props.setSelectedBundleProducts)
            }
          />
        </IconContainer>
      )}
      <InfoIconButton
        aria-label={i18nText.productInfoAriaLabel || 'Product Information'}
        onClick={() => {
          setOpenInfoModal(true)
          loadProductDescription({
            variables: {
              sku: props.product.sku,
              keys: productDescriptionKeys!,
            },
          })
        }}
      >
        <StyledIconInfo />
      </InfoIconButton>
      {openInfoModal && (
        <Modal
          open={openInfoModal}
          onClose={() => setOpenInfoModal(false)}
          i18nText={{
            closeLabel: i18nText.closeLabel,
            closeAriaLabel: i18nText.closeAriaLabel,
          }}
          gridColSpan={[12, 6, 8, 8]}
        >
          <React.Fragment>
            <Title>{i18nText.productInfoTitle}</Title>
            <SubTitle>{props.product.title}</SubTitle>
            <ProductDescriptionAccordion
              items={itemsWithTitle}
              details={{
                title: i18nText.productCategoryTitle,
                items: detailsWithTitle!,
              }}
            />
          </React.Fragment>
        </Modal>
      )}
      {isQuantitySelectorEnabled && props.maxProductsPerCategory > 1 && (
        <QuantityWrapper>
          <StyledQuantitySelector
            {...quantitySelector}
            quantityChangedCallback={(quantity) => {
              if (quantity) {
                handleUpdateQuantity(
                  props.product.sku,
                  quantity,
                  props.setSelectedBundleProducts,
                  setSelectedQuantity,
                )
              }
            }}
          />
        </QuantityWrapper>
      )}
    </div>
  )
}
